/**
 * Batching analytics client.
 *
 * Contract this upholds:
 *  - Analytics NEVER breaks the app. Every path is wrapped; a transport failure
 *    is swallowed, never retried forever, never surfaced to the UI.
 *  - Only typed events with allowlist-serialized payloads go on the wire
 *    (see events.ts) — there is no arbitrary-payload entry point.
 *  - Disabled builds do ZERO network work.
 *  - The client NEVER states whether the user is signed in. `authenticated` is
 *    derived server-side from the session (ato-api ingest); a browser value
 *    would be forgeable, and it is the authenticated cohort, the retention
 *    split and the login-conversion denominator that depend on it. Ingest does
 *    not accept the field at all.
 */
import {
  type AnalyticsEventMap,
  type AnalyticsEventName,
  IMMEDIATE_FLUSH_EVENTS,
  extractCapsuleId,
  extractCapsuleRef,
  extractLaunchId,
  isCapsuleScopedEvent,
  serializePayload,
} from "./events";
import {
  type AnalyticsEnvironment,
  getAnonymousId,
  getBrowserSessionId,
  newId,
  resolveAppVersion,
  resolveEnvironment,
} from "./identity";
import { accountApiBase, withConnectionAuth } from "../app/connection";

const MAX_BATCH = 20;
const FLUSH_INTERVAL_MS = 5_000;
const MAX_BUFFER = 100;

interface WireEvent {
  event_id: string;
  occurred_at: string;
  source: "ato-pwa";
  kind: AnalyticsEventName;
  /** `capsules.id` (ULID) — the admin dashboard's join key. */
  capsule_id?: string;
  /** "publisher/slug", when that is all the call site had. */
  capsule_ref?: string;
  launch_id?: string;
  environment: AnalyticsEnvironment;
  anonymous_id: string;
  browser_session_id: string;
  app_version?: string;
  payload: Record<string, unknown>;
}

let buffer: WireEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
/** At most one request in flight, so batches cannot interleave or duplicate. */
let inFlight: Promise<void> | null = null;
/** A flush was asked for while a request was already running. */
let flushPending = false;
/** …and at least one of those requests was a teardown flush. Sticky: once the
 *  page is going away it stays going away, so the drain below must keep using
 *  keepalive even for events buffered after the pagehide. */
let keepaliveRequested = false;
/** One retry per page load, total — never an unbounded resend loop. */
let retryBudget = 1;
let started = false;

function apiBase(): string {
  return accountApiBase();
}

/**
 * Enabled only when the build says so AND the environment is one we recognise.
 * `VITE_RUN_FUNNEL_ANALYTICS` is accepted during migration and removed in the
 * cleanup PR; either flag turns on exactly ONE transport (this client).
 */
export function isAnalyticsEnabled(): boolean {
  if (!resolveEnvironment()) return false;
  return (
    import.meta.env.VITE_ANALYTICS_ENABLED === "true" ||
    import.meta.env.VITE_RUN_FUNNEL_ANALYTICS === "true"
  );
}

/**
 * Identity headers for SERVER-recorded analytics.
 *
 * The server stamps most funnel events itself (a `/v1/launches` request IS the
 * launch), but it needs the browser's anonymous + tab-session identity to join
 * them to the client's ingest events — a `capsule_viewed → run_click`
 * conversion inside one tab, or an anonymous → signed-in merge. Attach these
 * headers to the user-action requests the server records from.
 *
 * The values are the SAME stable ids the ingest transport uses; the server
 * hashes them into salted buckets and never stores the raw values. Disabled
 * builds return nothing so they do ZERO network/header work.
 */
export function analyticsIdentityHeaders(): Record<string, string> {
  if (!isAnalyticsEnabled()) return {};
  return {
    "X-Ato-Anonymous-Id": getAnonymousId(),
    "X-Ato-Browser-Session": getBrowserSessionId(),
  };
}

function scheduleFlush(): void {
  if (timer !== null) return;
  timer = setTimeout(() => {
    timer = null;
    void flushAnalytics();
  }, FLUSH_INTERVAL_MS);
}

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

/** Record one typed event. Cheap and synchronous; never throws. */
export function recordAnalyticsEvent<K extends AnalyticsEventName>(
  name: K,
  input: AnalyticsEventMap[K],
): void {
  try {
    if (!isAnalyticsEnabled()) return;
    const environment = resolveEnvironment();
    if (!environment) return;

    const capsuleId = extractCapsuleId(name, input);
    const capsuleRef = extractCapsuleRef(input);
    // Last line of defence, below the types: a capsule-scoped event with no
    // subject is rejected by ingest with a 400, and a 400 is treated as "this
    // batch is malformed, do not retry" — so ONE such event would discard
    // every valid event batched beside it. Drop just this one instead.
    if (isCapsuleScopedEvent(name) && !capsuleId && !capsuleRef) return;
    const event: WireEvent = {
      event_id: newId(),
      occurred_at: new Date().toISOString(),
      source: "ato-pwa",
      kind: name,
      ...(capsuleId ? { capsule_id: capsuleId } : {}),
      ...(capsuleRef ? { capsule_ref: capsuleRef } : {}),
      ...(extractLaunchId(input) ? { launch_id: extractLaunchId(input) } : {}),
      environment,
      anonymous_id: getAnonymousId(),
      browser_session_id: getBrowserSessionId(),
      ...(resolveAppVersion() ? { app_version: resolveAppVersion()! } : {}),
      payload: serializePayload(name, input),
    };

    buffer.push(event);
    // Bounded memory: drop the OLDEST events, keeping the freshest signal.
    if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER);

    if (IMMEDIATE_FLUSH_EVENTS.has(name) || buffer.length >= MAX_BATCH) {
      void flushAnalytics();
    } else {
      scheduleFlush();
    }
  } catch {
    // Instrumentation must never break the caller.
  }
}

async function post(events: WireEvent[], keepalive: boolean): Promise<boolean> {
  try {
    const res = await fetch(`${apiBase()}/v1/telemetry/ingest`, withConnectionAuth({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events }),
      // Kept over sendBeacon: beacon cannot set Content-Type or send cookies
      // cross-origin the way this endpoint's auth expects.
      keepalive,
    }));
    // 4xx means the batch is malformed — retrying it would fail identically.
    return res.ok || (res.status >= 400 && res.status < 500);
  } catch {
    return false;
  }
}

/**
 * Send whatever is buffered. Safe to call at any time.
 *
 * When a request is already in flight the call is REMEMBERED rather than
 * dropped. Returning the in-flight promise alone lost the teardown: a
 * `pagehide` that landed mid-request left the events recorded after it waiting
 * on the ordinary 5s timer, and the page was destroyed before it fired. The
 * drain below therefore continues straight into the next batch instead of
 * rescheduling, and a `keepalive` request stays keepalive for the rest of the
 * drain.
 */
export async function flushAnalytics(keepalive = false): Promise<void> {
  if (keepalive) keepaliveRequested = true;
  if (inFlight) {
    // Not a no-op: the running drain picks this up when it finishes.
    flushPending = true;
    return inFlight;
  }
  if (buffer.length === 0) return;

  inFlight = drain(keepalive)
    .catch(() => {
      // Swallow — a failed flush must not reject into the app.
    })
    .finally(() => {
      inFlight = null;
      flushPending = false;
      // Anything recorded after the final batch was taken still needs a send.
      if (buffer.length > 0) {
        if (keepaliveRequested) void flushAnalytics(true);
        else scheduleFlush();
      }
    });

  return inFlight;
}

/** Send batches back-to-back until the buffer is empty. */
async function drain(keepalive: boolean): Promise<void> {
  do {
    flushPending = false;
    clearTimer();
    while (buffer.length > 0) {
      const batch = buffer.slice(0, MAX_BATCH);
      // Taken off the buffer BEFORE the await, so a concurrent record() cannot
      // land in this batch and be sent twice.
      buffer = buffer.slice(batch.length);
      const useKeepalive = keepalive || keepaliveRequested;
      const ok = await post(batch, useKeepalive);
      if (!ok) {
        if (retryBudget <= 0) return; // give up: drop the batch rather than loop
        retryBudget -= 1;
        const retried = await post(batch, useKeepalive);
        if (!retried) return;
      }
    }
  } while (flushPending);
}

/**
 * Install lifecycle flushes. Call once at startup. Idempotent, and a no-op in
 * disabled builds so those do zero work.
 */
export function startAnalytics(): void {
  if (started || !isAnalyticsEnabled()) return;
  started = true;
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void flushAnalytics(true);
    });
  }
  if (typeof window !== "undefined") {
    // pagehide (not unload) is the reliable teardown signal on mobile Safari.
    window.addEventListener("pagehide", () => void flushAnalytics(true));
  }
}

/** Test seam. */
export function resetAnalyticsForTests(): void {
  buffer = [];
  clearTimer();
  inFlight = null;
  flushPending = false;
  keepaliveRequested = false;
  retryBudget = 1;
  started = false;
}

/** Test seam — inspect what is buffered without sending. */
export function analyticsBufferSizeForTests(): number {
  return buffer.length;
}
